import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, ClickAwayListener, InputAdornment, Paper, TextField } from '@mui/material';
import classNames from 'classnames';
import config from 'config';
import React, { KeyboardEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { freeTextSearch, suggestionSearch } from 'store/actions/search.actions';
import { useAppDispatch } from 'store/hooks';
import { GTM, GTMElements, GTMLocations } from 'utils/analytics';
import { useIsScreenSizeMax } from 'utils/screen-size';

export type SearchSuggestion = {
  id: number | string | 'HEADER' | 'ALL';
  value: string;
  action?: (...args: any) => void;
  renderValue?: ReactElement;
  unselectable?: boolean;
};

export type SearchSelectOpts = {
  event: React.SyntheticEvent;
  close: () => void;
  searchValue: string;
};

export type SearchBarProps = {
  onSearch: (searchValue: string) => void;
  placeholder?: string;
  search?: string;
  suggestions?: SearchSuggestion[];
  onSelectSuggestion?: (value: SearchSuggestion, { event, close, searchValue }: SearchSelectOpts) => void;
  onSearchChange?: (searchValue: string, isSuggestionsVisible: boolean) => void;
};

const SearchBar = (props: SearchBarProps) => {
  const { onSearch, placeholder, search, onSelectSuggestion, suggestions, onSearchChange } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMobileScreen = useIsScreenSizeMax('md');

  const querySearchKey: string = search;
  const searchInputRef = useRef<HTMLInputElement>();
  const autoCompleteEnabled = config.get('autoComplete.enabled');

  const [isSuggestionsVisible, setSuggestionsVisible] = useState(false);
  const [searchValue, setSearchValue] = useState(querySearchKey || null);

  const highlightedSuggestion = useRef<SearchSuggestion>(null);

  // Update value
  const onChangeSearchValue = (value: string) => {
    setSearchValue(value);
  };

  // Reset
  const deleteSearchValue = () => {
    setSearchValue('');
  };

  const triggerSearch = () => {
    // If empty, set to null (otherwise keeping it '' will trigger redirect to index page when going directly to some url)
    if (!searchValue) {
      setSearchValue(null);
      onSearch(null);
    } else if (searchValue !== null) {
      // If not empty, do search
      onSearch(searchValue);
    }

    dispatch(freeTextSearch(searchValue));
  };

  const showAutocomplete = () => {
    if (!autoCompleteEnabled) {
      return;
    }

    setSuggestionsVisible(true);
  };

  const onClickSuggestion = (event: React.SyntheticEvent, suggestion: SearchSuggestion) => {
    if (suggestion.unselectable) {
      event.preventDefault();
    }

    onSelectSuggestion?.(suggestion, {
      event,
      close: () => setSuggestionsVisible(false),
      searchValue,
    });

    const suggestionId = suggestion.id.toString();
    const isBrand = suggestionId.includes('brand');
    const isCategory = suggestionId.includes('category');
    const suggestionType = isCategory ? 'category' : isBrand ? 'brand' : undefined;

    dispatch(suggestionSearch({ query: suggestion.value, suggestionType }));
  };

  useEffect(() => {
    // All this handling here needed so accessing any direct url wouldn't redirect to index page
    if (![null, undefined].includes(querySearchKey)) {
      setSearchValue(querySearchKey);
    } else {
      setSearchValue(null);
    }
  }, [querySearchKey]);

  useEffect(() => {
    highlightedSuggestion.current = null; // Reset highlights.
    onSearchChange?.(searchValue, isSuggestionsVisible);
  }, [searchValue, isSuggestionsVisible]);

  return (
    <ClickAwayListener
      onClickAway={(e: MouseEvent) => {
        if (!(e.target as HTMLElement).closest('.MuiAutocomplete-option')) {
          setSuggestionsVisible(false);
        }
      }}
    >
      <Autocomplete
        className="autocomplete"
        clearOnBlur={false}
        disablePortal
        options={suggestions}
        value={null}
        inputValue={searchValue || ''}
        getOptionLabel={(suggestion) => suggestion.value}
        getOptionDisabled={(suggestion) => suggestion.unselectable}
        renderOption={(optionProps, suggestion: SearchSuggestion) => (
          <li
            {...optionProps}
            key={suggestion.id + '-' + suggestion.value}
            onClick={(e) => onClickSuggestion(e, suggestion)}
            className={classNames(optionProps.className, {
              'custom-render': suggestion.renderValue,
              all: suggestion.id === 'ALL',
              header: suggestion.id === 'HEADER',
              unselectable: suggestion.unselectable,
            })}
          >
            {suggestion.renderValue || suggestion.value}
          </li>
        )}
        open={!!suggestions?.length && isSuggestionsVisible}
        filterOptions={(options) => options} // Do not filter.
        PaperComponent={useCallback(
          (props) => (
            <Paper {...props} className="autocomplete-option" />
          ),
          [],
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onHighlightChange={(_, suggestion: SearchSuggestion) => (highlightedSuggestion.current = suggestion || null)}
        renderInput={(params) => (
          <TextField
            {...params}
            onFocus={() => showAutocomplete()}
            onClick={() => showAutocomplete()}
            onBlur={() => setSuggestionsVisible(false)}
            fullWidth={true}
            inputRef={searchInputRef}
            placeholder={t(placeholder)}
            color="secondary"
            variant="standard"
            InputProps={{
              ...params.InputProps,
              className: 'search-input',
              startAdornment: (
                <InputAdornment position="start" sx={{ marginLeft: '10px' }}>
                  <SearchIcon className="search-icon" />
                </InputAdornment>
              ),
              endAdornment: searchValue && (
                <InputAdornment position="end" sx={{ marginRight: '10px' }}>
                  <CancelIcon className="cancel-icon" onClick={() => deleteSearchValue()} />
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
            value={searchValue || ''}
            onChange={(event) => onChangeSearchValue(event.target.value)}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter' && e.target) {
                e.preventDefault();
                e.stopPropagation();
                // If suggestion selected, do it's action.
                if (highlightedSuggestion.current) {
                  onClickSuggestion(e, highlightedSuggestion.current);
                } else {
                  // Search otherwise.
                  GTM.searchKeyword(GTMElements.SEARCH_BAR, GTMLocations.SEARCH_BAR, {
                    element_content: searchValue,
                  });

                  triggerSearch();
                }
                (e.target as HTMLElement).blur();
              }
            }}
            sx={{ color: '#979797', backgroundColor: '#F4F4F4', borderRadius: '100px', marginY: isMobileScreen ? '14px' : '0' }}
          />
        )}
      />
    </ClickAwayListener>
  );
};

export default SearchBar;
